import { IUnifiedOrderBlotterEntry } from 'apiConnector/blotters';
import type { PersistedTab, SecondaryPersistedTab } from 'apiConnector/tabs/tabs.model';
import { isSecondaryQuotesPersistedTab } from 'apiConnector/tabs/tabs.model';
import { BlotterType } from 'models/blotters/BlotterType';
import { getOrderById } from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import { secondaryMarketCpOrderFormTabFetched } from 'state/secondaryMarket/cpOrder';
import { secondaryMarketCpOrderInitFormFetchRequested } from 'state/secondaryMarket/cpOrder/referential/product/product.actions';
import { secondaryMarketDlpOrderTabContentPersistRequested } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.actions';
import { initialSecondaryMarketDlpOrderForm } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import {
  getSecondaryMarketProductClientFromRowId,
  getSecondaryMarketProductCustodianFromRowId,
  getSecondaryMarketProductIsinFromRowId,
  getSecondaryMarketProductTabId,
} from 'state/secondaryMarket/productSearch/productSearch.selectors';
import { initialQuoteRtForm } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import { secondaryMarketRtQuoteTabContentPersistRequested } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm/realtimeQuoteForm.actions';
import { secondaryMarketAstroProductFetchRequested } from 'state/secondaryMarket/referential/astroProduct/astroProduct.actions';
import { isSecondaryMarketAstroProductNotLoaded } from 'state/secondaryMarket/referential/astroProduct/astroProduct.selectors';
import { secondaryMarketDlpProductFetchRequested } from 'state/secondaryMarket/referential/dlpProduct/dlpProduct.actions';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import { getBcsProductKeyByWorkspace } from 'state/selectors/common/tabs';
import type { Thunk } from 'state/state.common';
import { SecondaryMarketQuotesTabType } from 'state/workspaces/model';

import { secondaryMarketProductRefreshRequested } from '../../../secondaryMarket/productSearch';
import { workspaceTabCreated, workspaceTabsFetched } from '../../workspace.actions';
import { secondaryMarketQuoteWorkspaceAddTab } from './secondaryMarketQuotes.actions';

export function secondaryQuoteByIsinOrFileCodeReloadThunk(
  isin: string | undefined,
  blotterType: BlotterType,
): Thunk<void> {
  return dispatch => {
    if (isin !== undefined) {
      if (blotterType === 'dlpProducts') {
        dispatch(secondaryDlpQuoteTabCreatedThunk(isin));
      } else {
        dispatch(secondaryRealtimeQuoteTabCreatedThunk(isin));
      }
    }
  };
}

export function secondaryQuoteRefreshRowThunk(rowId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const isin = getSecondaryMarketProductIsinFromRowId(state, rowId);

    if (isin !== undefined) {
      dispatch(secondaryMarketAstroProductFetchRequested(isin));
    }
    dispatch(secondaryMarketProductRefreshRequested([rowId], []));
  };
}

type MinimalOrderInfo = Pick<
  IUnifiedOrderBlotterEntry,
  'productIdSource' | 'productIdType' | 'productIdValue' | 'isin' | 'fileCode'
>;
export function secondaryOrderBlotterCPOrderTabCreatedThunk(orderInfo: string | MinimalOrderInfo): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const order = typeof orderInfo === 'string' ? getOrderById(state, orderInfo) : orderInfo;
    const tabId = getNewGuid();
    if (order === undefined) {
      return;
    }
    const key = `${order.productIdSource}:${order.productIdType}:${order.productIdValue}`;
    const tabNameId = order.isin ?? order.fileCode;

    dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, key, 'closePriceOrder'));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', 'closePriceOrder', `CP order ${tabNameId}`));
    const bcsKey = toBcsProductKey(key);
    if (bcsKey !== undefined) {
      dispatch(secondaryMarketCpOrderInitFormFetchRequested(tabId, bcsKey));
    }
  };
}

export function secondaryRealtimeQuoteOpenedFromProductSearchThunk(key: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const isin = getSecondaryMarketProductIsinFromRowId(state, key);
    if (isin !== undefined) {
      dispatch(secondaryRealtimeQuoteTabCreatedThunk(isin));
    }
  };
}

export function secondaryMarketAstroProductFetchRequestedByTabIdThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const key = getBcsProductKeyByWorkspace(state, 'secondaryMarketQuotes', tabId);

    if (key?.code && isSecondaryMarketAstroProductNotLoaded(state, key?.code)) {
      dispatch(secondaryMarketAstroProductFetchRequested(key?.code, tabId));
    }
  };
}

export function secondaryRealtimeQuoteTabCreatedThunk(isin: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();
    const key = `ISIN::${isin}`;
    dispatch(secondaryMarketAstroProductFetchRequested(isin, tabId));
    dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, key, 'realtimeQuote'));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', 'realtimeQuote', `Quote ${isin}`));
    dispatch(secondaryMarketRtQuoteTabContentPersistRequested(tabId, initialQuoteRtForm));
  };
}

export function secondaryDlpQuoteTabCreatedThunk(isin: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();
    const key = `ISIN::${isin}`;
    dispatch(secondaryMarketDlpProductFetchRequested(isin, tabId));
    dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, key, 'dlpProducts'));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', 'dlpProducts', `Quote ${isin}`));
    dispatch(secondaryMarketDlpOrderTabContentPersistRequested(tabId, initialSecondaryMarketDlpOrderForm));
  };
}

export function secondaryCPOrderTabCreatedFromProductSearchThunk(bcsKey: string, tradeWay?: TradeWay): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const tabId = getNewGuid();
    const bcsProductKey = toBcsProductKey(bcsKey);
    const productId = getSecondaryMarketProductTabId(state, bcsKey)!;
    const client = getSecondaryMarketProductClientFromRowId(state, bcsKey);
    const custodian = getSecondaryMarketProductCustodianFromRowId(state, bcsKey);

    dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, bcsKey, 'closePriceOrder'));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', 'closePriceOrder', `CP order ${productId}`));
    if (bcsProductKey !== undefined) {
      dispatch(secondaryMarketCpOrderInitFormFetchRequested(tabId, bcsProductKey, tradeWay, client, custodian));
    }
  };
}

export function secondaryQuotesWorkspaceTabsFetchedThunk(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  tabFetchRequestId?: string,
): Thunk<void> {
  return dispatch => {
    const commonTabs: SecondaryPersistedTab[] = tabs
      .filter(t => isSecondaryQuotesPersistedTab(t))
      .map(t => {
        dispatch(secondaryMarketQuoteWorkspaceAddTab(t.id, t.key, t.type as SecondaryMarketQuotesTabType));
        return t;
      });
    dispatch(workspaceTabsFetched(commonTabs, activeTabId, 'secondaryMarketQuotes', tabFetchRequestId));
    const cpOrderTabIds = commonTabs.filter(t => t.type === 'closePriceOrder').map(t => t.id);
    for (const tabId in cpOrderTabIds) {
      dispatch(secondaryMarketCpOrderFormTabFetched(tabId));
    }
  };
}
